// Runs one texture job into a bitmap. Shared by the texture worker (OffscreenCanvas, no yielding) and
// the main-thread fallback (yields between painter steps so the page stays responsive).

import { paintJob } from "./textureGen";
import type { Ctx2D } from "./textureGen";
import type { TextureJob } from "./textureSpec";

export type CanvasLike = OffscreenCanvas | HTMLCanvasElement;

/**
 * Paints `job` and returns it as `outSize` square bitmap, flipped so that three can upload it with
 * `flipY = false` (the picture's top row lands on the high v side, as for a CanvasTexture), straight
 * alpha (the decal atlas and grates have transparent pixels). `turn` is awaited between painter
 * steps; the worker passes none and runs the painter straight through.
 */
export async function renderJob(
  job: TextureJob,
  outSize: number,
  makeCanvas: (size: number) => CanvasLike,
  turn?: () => Promise<void>,
): Promise<ImageBitmap> {
  const canvas = makeCanvas(job.size);
  // An HTMLCanvasElement context has the same drawing API as the offscreen one.
  const g = canvas.getContext("2d") as unknown as Ctx2D | null;
  if (g === null) throw new Error("2D canvas context unavailable");
  const steps = paintJob(job, g);
  for (let step = steps.next(); step.done !== true; step = steps.next()) {
    if (turn !== undefined) await turn();
  }
  const options: ImageBitmapOptions = { imageOrientation: "flipY", premultiplyAlpha: "none" };
  if (outSize !== job.size) {
    options.resizeWidth = outSize;
    options.resizeHeight = outSize;
    options.resizeQuality = "high";
  }
  return createImageBitmap(canvas, options);
}
